import { ApiKey, ApiKeyCreateResponse } from "@/types/api-key";
import { PublicEnterpriseTenant } from "@/types/enterprise-tenant";
import {
  PrivateTenant,
  Tenant,
  TenantAccountingBasisUpdate,
  TenantBookingFeeCollectionUpdate,
  TenantGoogleTagManagerUpdate,
  TenantSecuritySettingsUpdate,
  TenantSelfSchedulingUpdate,
  TenantShopPresentationUpdate,
  TenantUpdate,
} from "@/types/tenant";

import { requestData } from "./api-config";

export async function fetchTenant(tenantName?: string): Promise<Tenant | null> {
  const headers = tenantName ? { TENANT: tenantName } : undefined;
  return await requestData<Tenant>("tenant/public", "get", { headers });
}

/** Which of the caller's clinics holds this Stripe connected account. */
export async function fetchStripeAccountOwner(
  stripeAccountId: string,
): Promise<{ subDomain: string } | null> {
  return await requestData<{ subDomain: string }>(
    "tenant/stripe-account-owner",
    "get",
    { searchParams: { stripe_account_id: stripeAccountId } },
  );
}

export async function fetchPrivateTenant(): Promise<PrivateTenant | null> {
  return await requestData<PrivateTenant>("tenant/private");
}

export async function fetchTenantHasParser(): Promise<boolean | null> {
  return await requestData<boolean>("tenant/parser");
}

export async function fetchTenantLogo(): Promise<string | null> {
  return await requestData<string>("tenant/logo");
}

export async function fetchEnterpriseTenant(): Promise<PublicEnterpriseTenant | null> {
  return await requestData<PublicEnterpriseTenant>("tenant/enterprise");
}

export async function updateTenant(
  tenantUpdate: TenantUpdate,
): Promise<Tenant | null> {
  return await requestData<Tenant>("tenant/update", "put", {
    json: tenantUpdate,
  });
}

/**
 * Sensitive settings live on their own endpoints rather than on tenant/update,
 * which accepts the union of every settings surface that posts to it and so
 * cannot be permissioned per field.
 */
export async function updateSecuritySettings(
  update: TenantSecuritySettingsUpdate,
): Promise<Tenant | null> {
  return await requestData<Tenant>("tenant/security-settings", "put", {
    json: update,
  });
}

export async function updateSelfScheduling(
  update: TenantSelfSchedulingUpdate,
): Promise<Tenant | null> {
  return await requestData<Tenant>("tenant/self-scheduling", "put", {
    json: update,
  });
}

export async function updateBookingFeeCollection(
  update: TenantBookingFeeCollectionUpdate,
): Promise<Tenant | null> {
  return await requestData<Tenant>("tenant/booking-fee-collection", "put", {
    json: update,
  });
}

export async function updateAccountingBasis(
  update: TenantAccountingBasisUpdate,
): Promise<Tenant | null> {
  return await requestData<Tenant>("tenant/accounting-basis", "put", {
    json: update,
  });
}

export async function updateGoogleTagManager(
  update: TenantGoogleTagManagerUpdate,
): Promise<Tenant | null> {
  return await requestData<Tenant>("tenant/google-tag-manager", "put", {
    json: update,
  });
}

export async function updateShopPresentation(
  update: TenantShopPresentationUpdate,
): Promise<Tenant | null> {
  return await requestData<Tenant>("tenant/shop-presentation", "put", {
    json: update,
  });
}

export async function uploadTenantLogo(file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append("file", file);

  return await requestData<string>("tenant/upload-logo", "post", {
    body: formData,
  });
}

export async function uploadSelfScheduleBanner(
  file: File,
): Promise<string | null> {
  const formData = new FormData();
  formData.append("file", file);

  return await requestData<string>(
    "tenant/upload-self-schedule-banner",
    "post",
    { body: formData },
  );
}

export async function removeSelfScheduleBanner(): Promise<boolean | null> {
  return await requestData<boolean>("tenant/self-schedule-banner", "delete");
}

export async function uploadShopBanner(file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append("file", file);

  return await requestData<string>("tenant/upload-shop-banner", "post", {
    body: formData,
  });
}

export async function removeShopBanner(): Promise<boolean | null> {
  return await requestData<boolean>("tenant/shop-banner", "delete");
}

export async function uploadFavicon(file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append("file", file);

  return await requestData<string>("tenant/upload-favicon", "post", {
    body: formData,
  });
}

export async function removeFavicon(): Promise<boolean | null> {
  return await requestData<boolean>("tenant/favicon", "delete");
}

export async function uploadReceiptLogo(file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append("file", file);

  return await requestData<string>("tenant/upload-receipt-logo", "post", {
    body: formData,
  });
}

// API Key Management (multi-key, RBAC-scoped)
export async function listApiKeys(): Promise<ApiKey[] | null> {
  return await requestData<ApiKey[]>("tenant/api-keys");
}

export async function createApiKey(data: {
  name: string;
  roleIds: string[];
}): Promise<ApiKeyCreateResponse | null> {
  return await requestData<ApiKeyCreateResponse>(
    "tenant/api-keys",
    "post",
    { json: data },
    false,
  );
}

export async function revokeApiKey(
  apiKeyId: string,
): Promise<{ message: string } | null> {
  return await requestData<{ message: string }>(
    `tenant/api-keys/${apiKeyId}`,
    "delete",
  );
}
